import { toast, type Id } from 'react-toastify';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import CheckIcon from '../assets/svg/checkIcon';
let customMessageToastId: Id | null = null;

const toastConfig = {
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: false,
  progress: undefined,
};

// Custom toast config for document feedback
const documentToastConfig = {
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
  progress: undefined,
  position: 'bottom-center',
  className: 'document-feedback-toast',
  bodyClassName: 'document-feedback-toast-body',
  icon: false,
  closeButton: false,
} as const;

/**
 * Show Success Message
 * @param msg
 */
export const showSuccessMsg = (msg: string): void => {
  toast.success(msg, toastConfig);
};

/**
 * Show Error Message
 * @param msg
 */
export const showErrorMsg = (
  msg: string = ERROR_MESSAGES.internalError
): void => {
  toast.error(msg, toastConfig);
};

/**
 * Show Warn Message
 * @param msg
 */
export const showWarnMsg = (msg: string): void => {
  toast.warn(msg, toastConfig);
};

/**
 * Show Info Message
 * @param msg
 */
export const showInfoMsg = (msg: string): void => {
  toast.info(msg, toastConfig);
};

/**
 * Show Document Feedback Toast
 * Custom toast for document create/update operations
 * @param action - 'Created' or 'Updated'
 * @param documentName - Name of the document
 */
export const showDocumentFeedback = (
  action: 'Created' | 'Updated',
  documentName: string
): void => {
  toast(
    <div className="flex flex-col gap-2 w-full sm:w-[500px]">
      <span className="text-base font-normal text-gray-700 tracking-[-0.16px]">
        {action}
      </span>
      <span className="text-xl font-medium text-gray-1000 tracking-[-0.2px]">
        {documentName}
      </span>
    </div>,
    documentToastConfig
  );
};

/**
 * Show Custom Message Toast with Document Style
 * Custom toast with white background for custom messages
 * @param message - Message to display
 */
export const showCustomMessageToast = (message: string): void => {
  const content = (
    <div className="flex flex-col gap-2 w-full sm:w-[500px]">
      <span className="text-base font-medium text-gray-1000">{message}</span>
    </div>
  );

  // If a custom-message toast is already visible, keep it and just reset its timer.
  // This avoids stacking and avoids an exit/enter animation.
  if (customMessageToastId !== null && toast.isActive(customMessageToastId)) {
    toast.update(customMessageToastId, {
      render: content,
      // Keep the same look & behavior
      ...documentToastConfig,
      onClose: () => {
        customMessageToastId = null;
      },
    });
    return;
  }

  customMessageToastId = toast(content, {
    ...documentToastConfig,
    onClose: () => {
      customMessageToastId = null;
    },
  });
};

/**
 * Show Bottom Toast with Success Icon
 * Custom toast with green checkmark icon for success messages
 * @param message - Message to display
 */
export const showBottomSuccessToast = (message: string): void => {
  toast(
    <div className="flex items-center gap-3 w-full sm:w-[500px]">
      <span className="relative bg-green-700 flex h-[25px] w-[25px] items-center justify-center rounded-full  group-[.completed]:border-green-600 text-white">
        <span className="flex items-center justify-center w-3 h-3 text-white">
          <CheckIcon />
        </span>
      </span>
      <span className="text-base font-normal text-gray-700">{message}</span>
    </div>,
    documentToastConfig
  );
};
